import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { DropdownLazyLoadEvent, DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { OverlayModule } from 'primeng/overlay';

import { APP_PERMISSION } from '@core/enum/permisson';
import { LeadNote, LeadReplyNote } from '@core/models/interfaces/lead/lead-notes';
import { PolicyDetails } from '@core/models/interfaces/lead/lead-policys';
import { NoteStatus } from '@core/models/interfaces/note';
import { NoteService } from '@core/services/note.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonComponent } from '@shared/components/button/button.component';
import { CustomAvatarComponent } from '@shared/components/custom-avatar/custom-avatar.component';
import { OptionStatusDropdownComponent } from '@shared/components/option-status-dropdown/option-status-dropdown.component';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { InputTextareaModule } from 'primeng/inputtextarea';

import { UserDetail } from '@core/models/interfaces/user';
import { Store } from '@ngrx/store';
import { checkTimeLimit } from '@shared/utils/checkTimeLimit';
import { AppState } from '@state/app.state';
import { selectUserInfo } from '@state/auth/auth.selectors';
import { Subject, takeUntil } from 'rxjs';
import { RawHtmlViewerComponent } from '@shared/components/raw-html-viewer/raw-html-viewer.component';
import { onlyGetTextFromHtml } from '@shared/utils/clipboard.util';
import { TooltipModule } from 'primeng/tooltip';
import { SearchInputComponent } from '@shared/components/search-input/search-input.component';
import { NoteStatusDropdownComponent } from '../option-note-status-dropdown/note-status-dropdown.component';

export interface NoteUpdateData {
  id: string;
  statusId: string;
  policyId: string;
  notes: string;
  isPin: boolean;
}

@Component({
  selector: 'app-lead-note-item',
  standalone: true,
  imports: [
    FormsModule,
    CommonModule,
    OptionStatusDropdownComponent,
    CustomAvatarComponent,
    InputTextModule,
    OverlayModule,
    TranslateModule,
    ButtonComponent,
    DropdownModule,
    MenuModule,
    InputTextareaModule,
    OverlayModule,
    NgxPermissionsModule,
    TooltipModule,
    SearchInputComponent,
    RawHtmlViewerComponent,
    NoteStatusDropdownComponent
  ],
  templateUrl: './lead-note-item.component.html',
  styleUrl: './lead-note-item.component.scss'
})
export class LeadNoteItemComponent implements OnInit, OnChanges, AfterViewInit {
  @Input({ required: true }) note: LeadNote;
  @Input() leadId = '';
  @Input() isExpanded = false;
  @Input() totalStatuses: number = 0;
  @Input() statuses: NoteStatus[] = [];
  @Input() policies: PolicyDetails[] = [];
  @Input() view: string;
  @Input() isQuickAction: boolean;
  @Input() isLeadNoteGrid: boolean;
  @Input() isLatestNote: boolean;
  @Input() keyword: string = '';

  @Output() onRefresh = new EventEmitter();
  @Output() deleteNote = new EventEmitter();
  @Output() refreshLayout = new EventEmitter();

  @ViewChild('noteElement') noteElement: ElementRef<HTMLDivElement>;

  readonly unsubscribe$ = new Subject();
  readonly Permissions = APP_PERMISSION;
  readonly translatePrefix = 'lead-details.notes.';
  readonly overviewTranslatePrefix = 'lead-details.overview-detail.';
  readonly policyTranslatePrefix = 'lead-details.policy.';

  readonly messagePrefix = 'lead-details.message.';
  readonly actions: MenuItem[] = [
    {
      id: 'EDIT',
      label: 'edit',
      icon: 'icon-wl-edit',
      command: () => this.onEdit(),
      permission: APP_PERMISSION.NOTE_APPEND_EDIT,
      permissionEditAnyNote: APP_PERMISSION.EDIT_ANY_NOTE
    },
    {
      id: 'DELETE',
      label: 'delete',
      icon: 'icon-wl-delete',
      styleClass: 'error-color',
      command: () => this.onDelete(this.note.id, 'NOTE'),
      permission: APP_PERMISSION.NOTE_APPEND_DELETE,
      permissionDeleteAnyNote: APP_PERMISSION.DELETE_ANY_NOTE
    }
  ];

  readonly appendActions: MenuItem[] = [
    {
      id: 'EDIT',
      label: 'edit',
      icon: 'icon-wl-edit',
      command: () => {
        this.isEditAppend = true;
        this.editingAppendId = this.appendIdSelected;
        const currentReply = this.note.replyNotes.find(reply => reply.id === this.editingAppendId);
        this.appendNote = currentReply?.notes ?? '';
      },
      permission: APP_PERMISSION.NOTE_APPEND_EDIT,
      permissionEditAnyNote: APP_PERMISSION.EDIT_ANY_NOTE
    },
    {
      id: 'DELETE',
      label: 'delete',
      icon: 'icon-wl-delete',
      styleClass: 'error-color',
      command: () => {
        this.onDelete(this.appendIdSelected, 'APPEND');
      },
      permission: APP_PERMISSION.NOTE_APPEND_DELETE,
      permissionDeleteAnyNote: APP_PERMISSION.DELETE_ANY_NOTE
    }
  ];

  isLarge = false;
  // isViewMore = true;
  isEditAppend = false;
  isEdit = false;
  isComment = false;
  isNoteSelected: boolean = false;
  isMenu = false;
  visible = false;

  appendIdSelected = '';
  editingAppendId = '';
  appendNote: string = '';
  noteValue = '';
  replyNotesValue: LeadReplyNote[] = [];
  isUpdating = false;
  isPin = false;
  statusInit: NoteStatus;
  statusId = '';
  policyId = '';
  isAddAppend: boolean = false;

  replyNote = '';
  userInfo: UserDetail;
  expandedReplies: { [key: string]: boolean } = {};
  hasPermissionAddAppend: boolean = false;
  isSubmitReply: boolean = false;
  hasPermissionEditAnyNote: boolean = false;
  hasPermissionDeleteAnyNote: boolean = false;

  replyNoteByUser?: LeadReplyNote;
  isReply: boolean = false;
  isEditReplyDraft: boolean = false;
  pageNoteStatuses: number = 0;
  isFetching: boolean = false;
  keywordNoteStatus: string = '';

  constructor(
    private noteService: NoteService,
    private messageService: MessageService,
    private translateService: TranslateService,
    private changeDetector: ChangeDetectorRef,
    private confirmationService: ConfirmationService,
    private permissionsService: NgxPermissionsService,
    private store: Store<AppState>
  ) {}

  ngOnInit(): void {
    this.store
      .select(selectUserInfo)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(userInfo => {
        if (userInfo) {
          this.userInfo = userInfo;
        }
      });

    this.note.replyNotes.forEach(reply => {
      this.expandedReplies[reply.id] = true;
    });

    this.hasPermissionAddAppend = Boolean(this.permissionsService.getPermission(APP_PERMISSION.NOTE_ADD_APPEND));
    this.hasPermissionEditAnyNote = Boolean(this.permissionsService.getPermission(APP_PERMISSION.EDIT_ANY_NOTE));
    this.hasPermissionDeleteAnyNote = Boolean(this.permissionsService.getPermission(APP_PERMISSION.DELETE_ANY_NOTE));

    this.replyNoteByUser = this.note.replyNotes?.find(item => item.isDraft && item.createdBy?.id === this.userInfo?.id);
  }

  ngAfterViewInit(): void {
    this.checkNoteIsLarge();
  }

  ngAfterViewChecked() {
    this.changeDetector.detectChanges();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['note']) {
      this.policyId = this.note?.policy?.id;
      this.statusId = this.note?.status?.id;
      this.isPin = Boolean(this.note.isPin);
      this.statusInit = { ...this.note.status };
      this.replyNoteByUser = this.note.replyNotes?.find(
        item => item.isDraft && item.createdBy?.id === this.userInfo?.id
      );
    }
    if (changes?.['note'] || changes?.['keyword']) {
      this.applyKeywordHighlight();
    }
  }

  onLoadMoreNoteStatuses(event: DropdownLazyLoadEvent) {
    if (this.isFetching) return;
    const { last } = event;
    const needLoadMore = last >= this.statuses.length - 1 && this.statuses.length < this.totalStatuses;

    if (!needLoadMore) return;
    this.pageNoteStatuses++;
    this.isFetching = true;
    this.fetchNoteStatuses('SCROLL');
  }

  fetchNoteStatuses(type: 'SCROLL' | 'SEARCH') {
    if (type === 'SEARCH') this.isFetching = true;
    const params = {
      keyword: this.keywordNoteStatus,
      page: this.pageNoteStatuses,
      size: 10
    };
    this.noteService
      .getNoteStatuses(params)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: res => {
          if (type === 'SCROLL') {
            this.statuses = [...this.statuses, ...res.data.content];
          } else {
            this.statuses = res.data.content;
          }
          this.totalStatuses = res.data.total;
          this.isFetching = false;
        },
        error: () => {
          this.isFetching = false;
        }
      });
  }

  onSearchNoteStatuses(keyword: string) {
    this.pageNoteStatuses = 0;
    this.keywordNoteStatus = keyword;
    this.fetchNoteStatuses('SEARCH');
  }

  applyKeywordHighlight(): void {
    this.isComment = false;
    this.noteValue = this.note.notes;
    this.replyNotesValue = this.note.replyNotes.map(reply => ({ ...reply }));

    if (this.keyword && this.keyword.trim()) {
      const escapedKeyword = this.keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const pattern = escapedKeyword.replace(/\s+/g, '\\s+');
      const regex = new RegExp(pattern, 'gi');

      this.noteValue = this.noteValue.replace(regex, match => `<strong>${match}</strong>`);
      this.replyNotesValue.forEach(reply => {
        reply.notes = reply.notes.replace(regex, match => `<strong>${match}</strong>`);
      });
      if (this.replyNotesValue.some(reply => reply.notes.includes('<strong>'))) {
        this.isComment = true;
      }
    }
  }

  checkNoteIsLarge() {
    this.isLarge = this.noteElement?.nativeElement?.clientHeight > 85;
  }

  onCancel() {
    this.isEdit = false;
    this.noteValue = this.note.notes;
    this.statusInit = { ...this.note.status };
    this.applyKeywordHighlight();
    this.refreshLayout.emit();
  }

  onDelete(id: string, type: 'NOTE' | 'APPEND') {
    this.confirmationService.confirm({
      header: this.translateService.instant(this.messagePrefix + (type === 'NOTE' ? 'delete-note' : 'delete-append')),
      message: this.translateService.instant(
        this.messagePrefix + (type === 'NOTE' ? 'confirm-delete-note' : 'confirm-delete-append')
      ),
      acceptButtonStyleClass: 'btn-xl btn-danger',
      rejectButtonStyleClass: 'btn-xl btn-outline-secondary',
      rejectLabel: this.translateService.instant('action.cancel'),
      acceptLabel: this.translateService.instant('action.delete'),
      acceptIcon: 'none',
      rejectIcon: 'none',

      accept: () => {
        this.noteService.deleteLeadNoteById(id).subscribe({
          next: () => {
            if (type === 'NOTE') {
              this.onRefresh.emit();
              this.deleteNote.emit();
            } else {
              this.note.replyNotes = [...this.note.replyNotes].filter(item => item.id !== id);

              if (this.replyNoteByUser?.id === id) {
                this.replyNoteByUser = undefined;
              }
            }

            this.messageService.add({
              severity: 'success',
              detail: this.translateService.instant(
                this.messagePrefix + (type === 'NOTE' ? 'delete-note-success' : 'delete-append-success')
              )
            });
            this.replyNotesValue = this.replyNotesValue.filter(item => item.id !== id);
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              detail: this.translateService.instant(
                this.messagePrefix + (type === 'NOTE' ? 'delete-note-failed' : 'delete-append-failed')
              )
            });
          }
        });
      }
    });
  }

  onEdit() {
    this.isEdit = true;
    this.noteValue = this.note.notes;
    this.refreshLayout.emit();
  }

  onSelectReply(reply: LeadReplyNote) {
    this.appendIdSelected = reply.id;
    if (!this.isEditAppend || this.editingAppendId === reply.id) {
      this.appendNote = reply.notes;
    }
  }

  onEditAppend(event: Event, index: number) {
    event.preventDefault();
    this.noteService
      .updateLeadNoteById(this.editingAppendId, { notes: this.appendNote })
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (res) => {
          this.note.replyNotes[index].notes = this.appendNote;
          this.appendNote = '';
          this.isEditAppend = false;
          this.editingAppendId = '';
          this.messageService.add({
            severity: 'success',
            detail: 'Update append note successfully'
          });
          this.replyNotesValue[index].notes = this.note.replyNotes[index].notes;
          this.onRefresh.emit(res.data)
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            detail: 'Update append note failed'
          });
        }
      });
  }

  onEditAppendDraft(event: Event) {
    event.preventDefault();
    if (this.replyNoteByUser && this.replyNoteByUser?.notes?.trim()) {
      const index = this.note.replyNotes.findIndex(item => item.id === this.replyNoteByUser?.id);
      this.noteService
        .updateLeadNoteById(this.replyNoteByUser?.id, { notes: this.replyNoteByUser.notes, isDraft: false })
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe({
          next: (res) => {
            this.note.replyNotes[index].notes = this.replyNoteByUser?.notes || '';
            this.messageService.add({
              severity: 'success',
              detail: 'Update append note successfully'
            });
            this.replyNotesValue[index].notes = this.replyNoteByUser?.notes || '';
            this.replyNotesValue[index].isDraft = false;
            this.replyNoteByUser = undefined;
            this.onRefresh.emit(res.data)
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              detail: 'Update append note failed'
            });
          }
        });
    }
  }

  onCancelEditAppend() {
    this.appendNote = '';
    this.isEditAppend = false;
    this.editingAppendId = '';
  }

  onCancelAddAppend() {
    this.replyNote = '';
    this.isAddAppend = false;
  }

  onUpdate() {
    this.isUpdating = true;
    this.noteService
      .updateLeadNoteById(this.note.id, {
        statusId: this.statusId || this.note.status?.id,
        notes: this.noteValue,
        isPin: this.isPin,
        leadId: this.leadId,
        policyId: this.policyId,
        isDraft: false
      })
      .subscribe({
        next: res => {
          this.isUpdating = false;
          this.messageService.add({
            severity: 'success',
            detail: this.translateService.instant(this.messagePrefix + 'update-note-success')
          });
          this.note = res?.data;

          this.isEdit = false;
          this.checkNoteIsLarge();
          this.onRefresh.emit(res.data);
        },
        error: () => {
          this.isUpdating = false;
          this.messageService.add({
            severity: 'error',
            detail: this.translateService.instant(this.messagePrefix + 'update-note-failed')
          });
        }
      });
  }

  onReply(event: Event) {
    event.preventDefault();
    this.isReply = true;
    if (!this.replyNote.trim() || this.isSubmitReply) return;
    this.isSubmitReply = true;
    this.noteService.replyLeadNote(this.note.id, this.replyNote).subscribe({
      next: res => {
        this.messageService.add({
          severity: 'success',
          detail: this.translateService.instant(this.messagePrefix + 'reply-success')
        });
        this.expandedReplies[res.data.id] = true;
        this.note.replyNotes = [...this.note.replyNotes, res.data];
        this.replyNotesValue = [...this.replyNotesValue, res.data];
        this.refreshLayout.emit();
        this.replyNote = '';
        this.isSubmitReply = false;
        this.isAddAppend = false;
        this.onRefresh.emit(res.data)
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          detail: this.translateService.instant(this.messagePrefix + 'reply-failed')
        });
        this.isSubmitReply = false;
        this.isAddAppend = false;
      }
    });
  }

  onReplyDraft() {
    this.isReply = true;
    if (!this.replyNote.trim()) return;
    this.noteService.replyLeadNoteDraft(this.note.id, this.replyNote).subscribe(res => {
      this.replyNoteByUser = res.data;
      this.onRefresh.emit(this.replyNoteByUser)
    });
  }

  onAddAppend() {
    this.isAddAppend = !this.isAddAppend;
    // this.refreshLayout.emit();
  }

  onPin() {
    if (this.isUpdating) return;

    this.isUpdating = true;
    this.isPin = !this.isPin;
    this.noteService
      .updateLeadNoteById(this.note.id, {
        statusId: this.note.status?.id ?? '00000000-0000-0000-0000-000000000000',
        notes: this.note.notes,
        isPin: this.isPin,
        leadId: this.leadId,
        policyId: this.policyId
      })
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            detail: this.translateService.instant(
              this.messagePrefix + (this.isPin ? 'pin-note-success' : 'unpin-note-success')
            )
          });
          this.isUpdating = false;
          this.onRefresh.emit();
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            detail: this.translateService.instant(
              this.messagePrefix + (this.isPin ? 'pin-note-failed' : 'unpin-note-failed')
            )
          });
          this.isUpdating = false;
        }
      });
  }

  isCreatedIn30Minutes(createdById?: string, createdAt?: string) {
    return this.userInfo.id === createdById && checkTimeLimit(createdAt, 30);
  }

  isCreatedByOwner(createdById?: string) {
    return this.userInfo.id === createdById;
  }

  get fullNameCreatedBy() {
    return this.note.createdBy?.fullName || '';
  }

  toggleReply(replyId: string) {
    this.expandedReplies[replyId] = !this.expandedReplies[replyId];
  }

  onPasteText(event: ClipboardEvent) {
    onlyGetTextFromHtml(event);
  }

  // @HostListener('window:beforeunload', ['$event'])
  // async handleBeforeUnload(event: BeforeUnloadEvent) {
  //   if (!this.isReply && this.replyNote.trim()) {
  //   }
  // }

  onEditReplyDraft() {
    this.isEditReplyDraft = !this.isEditReplyDraft;
  }

  onUpdateNoteDraft() {
    if (!this.note?.isDraft || !this.noteValue.trim() || this.note.notes === this.noteValue) return;
    this.isUpdating = true;
    this.noteService
      .updateLeadNoteById(this.note.id, {
        statusId: this.statusId || this.note.status?.id,
        notes: this.noteValue,
        isPin: false,
        leadId: this.leadId,
        policyId: this.policyId,
        isDraft: true
      })
      .subscribe();
  }

  onEditAppendDraftContinueDraft() {
    if (this.replyNoteByUser && this.replyNoteByUser?.notes?.trim()) {
      this.noteService
        .updateLeadNoteById(this.replyNoteByUser?.id, { notes: this.replyNoteByUser.notes, isDraft: true })
        .subscribe();
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  async unloadNotification($event: BeforeUnloadEvent) {
    // native protection for refresh/tab close
    if (this.note?.isDraft) {
      this.onUpdateNoteDraft();
    }
    if (this.isEditReplyDraft) {
      this.onEditAppendDraftContinueDraft();
    }
    if (!this.isReply) {
      this.onReplyDraft();
    }

    if ((this.noteValue && this.note.notes !== this.noteValue) || this.replyNote) {
      $event.preventDefault();
      $event.returnValue = true;
    }
  }

  ngOnDestroy(): void {
    if (!this.isReply) {
      this.onReplyDraft();
    }
    if (this.isEditReplyDraft) {
      this.onEditAppendDraftContinueDraft();
    }
    if (this.note?.isDraft) {
      this.onUpdateNoteDraft();
    }

    this.unsubscribe$.next(null);
    this.unsubscribe$.complete();
  }
}
